import { RxCollection, RxDocument, RxError } from 'rxdb';

/**
 * RxDocument returns rejected promise if the doc has already been removed before
 * This behavior maybe undesired when in a race condition circumstance a document gets deleted twice.
 * Instead, if doc has already been removed, just return false, not throw
 * */
export const safeRemoveDocument = async <T>(doc: RxDocument<T>) => {
	try {
		await doc.incrementalRemove();
		return true;
	} catch (e) {
		if ((e as RxError).code === 'DOC13') {
			return false;
		} else {
			throw e;
		}
	}
};

/**
 * Safely performs bulkUpsert operation and logs any errors that occur.
 * bulkUpsert doesn't throw errors but returns them in the error array of the result.
 * This helper checks for errors, logs them, and throws an exception if errors occurred.
 *
 * @param collection - The RxDB collection to perform bulkUpsert on
 * @param documents - Array of documents to upsert
 * @returns The bulkUpsert result
 * @throws Error if any documents failed to upsert
 * */
export const safeBulkUpsert = async <T>(
	collection: RxCollection<T>,
	documents: T[]
) => {
	const result = await collection.bulkUpsert(documents);

	if (result.error.length > 0) {
		console.error(`[RxDB bulkUpsert ERROR] ${collection.name}`, {
			errors: result.error,
			successCount: result.success.length,
			totalCount: documents.length,
		});

		result.error.forEach((err) => {
			console.error('Document error:', err);
		});

		// Throw error to prevent silent failures
		throw new Error(
			`bulkUpsert failed for ${result.error.length} of ${documents.length} documents in ${collection.name}`
		);
	}

	return result;
};
